import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Layout from "../../components/layout";
import bookingService from "../../services/booking";
import { DecryptData, EncryptData, jsonParser } from "../../helpers/helper";
import {
  Box,
  Typography,
  Divider,
  Chip,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  AccountCircle,
  CalendarMonth,
  Payments,
  CreditCard,
  AccountBalanceWallet,
} from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import { printStaffReceipts } from "../../components/print";

export default function ViewBooking() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);

  // Modal State
  const [openPendingModal, setOpenPendingModal] = useState(false);
  const [payAmount, setPayAmount] = useState<number | string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("cash");
  const [amountError, setAmountError] = useState<string>("");
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const fetchBooking = async () => {
    setLoading(true);
    try {
      const encrypted = await bookingService.findOne(id);
      const decrypted = await DecryptData(encrypted);
      const parsed = jsonParser(decrypted)[0];

      const paymentMethodsString = (parsed.paymentMethods || []).join(", ");
      let paymentCode = "";
      const method = paymentMethodsString?.toLowerCase() || "";

      if (method === "cash") {
        paymentCode = "C";
      } else if (
        method === "online" ||
        method === "pos" ||
        method === "online,pos" ||
        method === "pos,online"
      ) {
        paymentCode = "D";
      } else if (method === "") {
        paymentCode = "R"; // For 100% discounted / empty payment methods
      } else {
        paymentCode = "CD"; // For mixed payment methods
      }

      const dateObj = new Date(parsed.bookingDate || Date.now());
      const day = String(dateObj.getDate()).padStart(2, "0");
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const year = dateObj.getFullYear();
      const shortYear = String(year).slice(-2);

      const receiptNo = `BO-M-${month}${day}${shortYear}-${paymentCode}-${parsed.id}`;

      const finalResult = {
        ...parsed,
        comments: parsed.comments ? await DecryptData(parsed.comments) : "",
        reason: parsed.reason ? await DecryptData(parsed.reason) : "",
        cart: parsed.services.map((item: any) => ({
          cartId: String(item.serviceId),
          id: Number(item.serviceId),
          name: item.serviceName,
          price: Number(item.servicePrice),
          qty: Number(item.quantity),
          type: item.itemType === "individual" ? "independent" : item.itemType,
        })),
        items: parsed.services.map((item: any) => ({
          ...item,
          variationText:
            item.variations.length > 0
              ? item.variations
                  .map(
                    (v: any) => `${v.variationName} (${v.variationTypeName})`
                  )
                  .join(", ")
              : "",
        })),
        receiptNo,
      };

      setBooking(finalResult);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
  }, [id]);

  if (loading)
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );

  if (!booking)
    return (
      <Layout>
        <Typography>Booking not found.</Typography>
      </Layout>
    );

  const totalPaid =
    Number(booking.cashAmount || 0) +
    Number(booking.onlineAmount || 0) +
    Number(booking.posAmount || 0);

  const handlePrintReceipt = async () => {
    const paymentMethodsString = booking.paymentMethods.join(", ");

    let paymentCode = "";
    const method = paymentMethodsString?.toLowerCase() || "";

    if (method === "cash") {
      paymentCode = "C";
    } else if (
      method === "online" ||
      method === "pos" ||
      method === "online,pos" ||
      method === "pos,online"
    ) {
      paymentCode = "D";
    } else {
      paymentCode = "CD";
    }

    const dateObj = new Date(booking.bookingDate);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    const shortYear = String(year).slice(-2);
    const customReceiptId = `BO-M-${month}${day}${shortYear}-${paymentCode}-${booking.id}`;

    const subtotalBeforeTax = Number(booking.grandTotal) - Number(booking.tax);
    const discountInPercent =
      booking.grandTotal > 0
        ? (Number(booking.discount) / booking.grandTotal) * 100
        : 0;

    const payload: any = {
      totalExcTax: Number(subtotalBeforeTax),
      customerName: booking.customerName,
      cashierName: booking.cashierName,
      services: booking.cart,
      reason: booking.reason,
      subtotal: Number(subtotalBeforeTax),
      praTax: Number(booking.tax),
      totalPrice: Number(booking.grandTotal),
      GrandTotal: Number(booking.grandTotal),
      discountInPercentage: discountInPercent,
      discountInAmount: Number(booking.discount),
      amountPaid: Number(totalPaid),
      pending: Number(booking.grandTotal) - totalPaid,
      customReceiptId,
      date: booking.bookingDate,
    };
    printStaffReceipts(payload);
  };

  // Open Modal Handler
  const handleOpenPendingModal = () => {
    setPayAmount(booking.pendingAmount || 0);
    setPaymentMethod("cash");
    setAmountError("");
    setOpenPendingModal(true);
  };

  // Close Modal Handler
  const handleClosePendingModal = () => {
    setOpenPendingModal(false);
    setAmountError("");
  };

  // Amount Change & Real-Time Validation Handler
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPayAmount(value);

    const numericVal = Number(value);
    const pending = Number(booking.pendingAmount || 0);

    if (!value || numericVal <= 0) {
      setAmountError("Amount must be greater than 0");
    } else if (numericVal > pending) {
      setAmountError(`Amount cannot exceed pending amount (Rs. ${pending})`);
    } else {
      setAmountError("");
    }
  };

  // Submit Payment Handler
  const handleSubmitPayment = async () => {
    const numericAmount = Number(payAmount);
    const pendingAmount = Number(booking.pendingAmount || 0);

    if (!payAmount || numericAmount <= 0) {
      setAmountError("Amount must be greater than 0");
      return;
    }

    if (numericAmount > pendingAmount) {
      setAmountError(
        `Amount cannot exceed pending amount (Rs. ${pendingAmount})`
      );
      return;
    }

    try {
      setSubmittingPayment(true);

      const payload = {
        bookingId: booking.id,
        amount: numericAmount,
        paymentMethod,
      };

      const encryptedData = EncryptData(JSON.stringify(payload));
      // Encrypt if required by your backend
      await bookingService.clearPendingDues({ encryptedData });

      console.log("Submitting Payment Payload:", payload);

      fetchBooking();

      handleClosePendingModal();
    } catch (err) {
      console.error("Error clearing pending dues:", err);
    } finally {
      setSubmittingPayment(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 850, mx: "auto", p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            border: "1px solid #eee",
            bgcolor: "#fff",
          }}
        >
          {/* HEADER */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: "#2d3436" }}
              >
                Invoice
              </Typography>
              <Typography color="textSecondary" variant="body2">
                Reference: #{booking.receiptNo}
              </Typography>
            </Box>
            <Stack direction="column" alignItems="flex-end" spacing={1}>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div>
                  <button
                    style={{
                      border: "none",
                      color: "#af0000",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={handlePrintReceipt}
                    title="Print Receipt"
                  >
                    <PrintIcon fontSize="small" />
                  </button>
                </div>
                <Chip
                  label={booking.status?.toUpperCase() || "PENDING"}
                  color={booking.status === "completed" ? "success" : "warning"}
                  sx={{ fontWeight: 700, borderRadius: "8px" }}
                />
              </div>

              <Typography variant="caption" sx={{ color: "#b2bec3" }}>
                Served by: {booking.cashierName}
              </Typography>
            </Stack>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* CUSTOMER & DATE */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <AccountCircle fontSize="small" sx={{ color: "#719879" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  CUSTOMER
                </Typography>
              </Stack>
              <Typography variant="body1">{booking.customerName}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <CalendarMonth fontSize="small" sx={{ color: "#719879" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  DATE & TIME
                </Typography>
              </Stack>
              <Typography variant="body1">{booking.bookingDate}</Typography>
            </Grid>
          </Grid>

          {/* SERVICES RENDERED */}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 2, color: "#719879" }}
          >
            SERVICES
          </Typography>
          <Box sx={{ mb: 4 }}>
            {booking.items.map((item: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  py: 1.5,
                  borderBottom: "1px dashed #eee",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {item.serviceName}
                  </Typography>
                  {item.variationText && (
                    <Typography variant="caption" sx={{ color: "#636e72" }}>
                      {item.variationText}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700 }}>
                  x{item.quantity || 1}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* PAYMENT BREAKDOWN */}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 2, color: "#719879" }}
          >
            PAYMENT METHODS
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #f1f1f1",
                  textAlign: "center",
                }}
              >
                <Payments sx={{ color: "#719879", mb: 0.5 }} />
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  CASH
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  Rs. {Number(booking.cashAmount || 0).toFixed(0)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #f1f1f1",
                  textAlign: "center",
                }}
              >
                <AccountBalanceWallet sx={{ color: "#0984e3", mb: 0.5 }} />
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  ONLINE
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  Rs. {Number(booking.onlineAmount || 0).toFixed(0)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #f1f1f1",
                  textAlign: "center",
                }}
              >
                <CreditCard sx={{ color: "#6c5ce7", mb: 0.5 }} />
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  POS / CARD
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  Rs. {Number(booking.posAmount || 0).toFixed(0)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* TOTALS & SUMMARY */}
          <Box
            sx={{
              bgcolor: "#2d3436",
              color: "#fff",
              p: 3,
              borderRadius: 3,
              mb: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Subtotal
                </Typography>
                <Typography className="cartItems" variant="body2">
                  Rs. {booking.subtotal}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Discount
                </Typography>
                <Typography
                  className="cartItems"
                  variant="body2"
                  sx={{ color: "#ff7675" }}
                >
                  - Rs. {Number(booking.discount).toLocaleString()}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Tax (8%)
                </Typography>
                <Typography className="cartItems" variant="body2">
                  Rs. {booking.tax}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Grand Total
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: "#719879" }}
                >
                  Rs. {Number(booking.grandTotal).toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* BALANCE BAR */}
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: "#f9fbf9",
              border: "1px solid #e8f0e8",
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Total Paid
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "#719879", fontWeight: 700 }}
                >
                  Rs. {totalPaid.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="textSecondary">
                  Balance Due
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: booking.pendingAmount > 0 ? "#d63031" : "#719879",
                    fontWeight: 700,
                  }}
                >
                  Rs. {booking.pendingAmount}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* FOOTER ACTIONS */}
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            {booking.pendingAmount > 0 && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenPendingModal}
                sx={{ borderRadius: "10px", textTransform: "none" }}
              >
                Clear Pending Dues
              </Button>
            )}

            <Button
              variant="outlined"
              fullWidth
              onClick={() => history.push("/bookings")}
              sx={{ borderRadius: "10px", textTransform: "none" }}
            >
              Return to Bookings
            </Button>
          </Stack>
        </Paper>

        {/* CLEAR PENDING DUES MODAL */}
        <Dialog
          open={openPendingModal}
          onClose={handleClosePendingModal}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            Clear Pending Dues
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Pending Amount Due:{" "}
                <strong style={{ color: "#d63031" }}>
                  Rs. {booking.pendingAmount}
                </strong>
              </Typography>

              {/* Amount Field */}
              <TextField
                fullWidth
                label="Amount"
                type="number"
                variant="outlined"
                value={payAmount}
                onChange={handleAmountChange}
                error={Boolean(amountError)}
                helperText={amountError}
                inputProps={{
                  max: booking.pendingAmount,
                  min: 0,
                }}
                sx={{ mb: 2.5 }}
              />

              {/* Payment Method Selector */}
              <FormControl fullWidth>
                <InputLabel id="payment-method-label">
                  Payment Method
                </InputLabel>
                <Select
                  labelId="payment-method-label"
                  value={paymentMethod}
                  label="Payment Method"
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="pos">POS / Card</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleClosePendingModal}
              disabled={submittingPayment}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitPayment}
              disabled={
                Boolean(amountError) ||
                !payAmount ||
                Number(payAmount) <= 0 ||
                submittingPayment
              }
              sx={{
                bgcolor: "#719879",
                "&:hover": { bgcolor: "#5c7d63" },
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              {submittingPayment ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Pay Now"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
